import { getFlattenCategoryValues } from '@utils/array'
import * as ListDataManager from 'dataContainer/listDataManager'
import { NestedColumnValue } from 'dataContainer/listDataManager/listDataManagerTypes'
import { v4 as uuidv4 } from 'uuid'
import { IDatasetMetadataResponse } from '@global/api/types'
import { IDatasetById } from '@global/api/transforms'
import { ErrorCorrectionRow } from '@global/api/error-correction/type'
import { ReadGridRow } from '@datasets/dataset-details/datasetData/context/types'
import { DatasetValue } from '@datasets/utils/dataset-utils'
import { DatasetWithDatasetRows } from '@global/types'

interface IDatasetValues {
  idx: number
  filteredColumns: IColumn[]
  rows: IRow[]
  templateFields: ITemplateField[]
}

interface IColumn {
  governanceType: string
  fieldId: string | number
}

interface IRow {
  data: Record<string | number, { value: string }>
}

interface IListGovernace {
  list_column_id: string | number
  list_id: number
  lists: {
    type: string
  }
}

interface ITemplateField {
  field_id: string | number
  field?: {
    list_governance?: IListGovernace
  }
}

interface IStructuredLists {
  templateFields: ITemplateField[]
}

export function getDatasetValues({ idx, filteredColumns, rows, templateFields }: IDatasetValues) {
  const validatedLists: IValidatedListReturn[] = []
  const structuredLists = getStructuredLists({ templateFields })

  const result = filteredColumns.map(column => {
    const { fieldId } = column
    const currentRow = rows[idx]

    if (column.governanceType === 'list') {
      const templateField = templateFields.find(field => field.field_id === fieldId)

      let list = validatedLists.find(
        list => list.listId === templateField?.field?.list_governance?.list_id,
      )

      if (!list && currentRow && templateField) {
        const validatedList = getValidatedList({
          currentRow,
          templateField,
          lists: structuredLists,
        })

        validatedLists.push(validatedList)
        list = validatedList
      }

      return {
        field_id: fieldId,
        cell_value: list?.values[fieldId],
      }
    }

    const currentCell = currentRow?.data[fieldId]

    return {
      field_id: fieldId,
      cell_value: currentCell?.value ?? '',
    }
  })
  return result
}

interface IList {
  listId: number
  listType: string
  values: NestedColumnValue[] | Record<number, string>[]
  columnList: Record<string, { fieldId: string | number; precedence: number }>
  fieldList: Record<string, { columnListId: string | number; precedence: number }>
}

function getStructuredLists({ templateFields }: IStructuredLists) {
  const lists: Record<number, IList> = {}
  templateFields
    .map(templateField => ({
      field_id: templateField.field_id,
      list_governance: templateField.field?.list_governance,
    }))
    .filter(
      (
        templateField,
      ): templateField is { field_id: string | number; list_governance: IListGovernace } =>
        !!templateField.list_governance,
    )
    .forEach(({ field_id, list_governance }) => {
      const columnListId = Number(list_governance.list_column_id)
      const listId = list_governance.list_id
      const metadata = ListDataManager.getListMetadata(listId)
      lists[listId] = {
        ...lists[listId],
        listId,
        columnList: {
          ...lists[listId]?.columnList,
        },
        fieldList: {
          ...lists[listId]?.fieldList,
        },
        listType: list_governance.lists.type,
        values: [],
      }
      lists[listId].columnList[columnListId] = {
        fieldId: field_id,
        precedence: metadata.columnsById[columnListId]?.precedence ?? 0,
      }
      lists[listId].fieldList[field_id] = {
        columnListId,
        precedence: metadata.columnsById[columnListId]?.precedence ?? 0,
      }
    })

  const structuredLists: IList[] = []
  for (const [listId, list] of Object.entries(lists)) {
    const listData = ListDataManager.getListData(listId)
    const nestedValues = ListDataManager.getListNestedValues(listId)
    const columnIds = Object.entries(list.columnList)
      .sort(([, a], [, b]) => a.precedence - b.precedence)
      .map(([key]) => key)

    const iterateNestedValues = (nestedValues: NestedColumnValue[], columnIdsIndex = 0) => {
      const newValues: NestedColumnValue[] = []
      nestedValues.forEach(row => {
        if (row.columnId === columnIds[columnIdsIndex]) {
          const value: NestedColumnValue = { ...row, children: [] }
          value[row.columnId] = row.value
          value.children = iterateNestedValues(row.children, columnIdsIndex + 1)
          newValues.push(value)
        } else {
          newValues.push(...iterateNestedValues(row.children, columnIdsIndex))
        }
      })
      return newValues
    }

    let values: NestedColumnValue[] | Record<number, string>[] = [] // TODO: fix this
    switch (list.listType) {
      case 'drop-down':
      case 'dependent':
        values = Object.values(listData.rowsWithColumnIds)
        break
      case 'drill-down':
        values = iterateNestedValues(nestedValues)
        break
    }
    list.values = values
    structuredLists.push(list)
  }
  return structuredLists
}

interface IValidatedList {
  currentRow: IRow
  templateField: ITemplateField
  lists: IList[]
}

interface IValidatedListReturn {
  listId: number
  values: Record<string, unknown>
}

function getValidatedList({
  currentRow,
  templateField,
  lists,
}: IValidatedList): IValidatedListReturn {
  if (!templateField.field?.list_governance) {
    return { listId: -1, values: {} }
  }

  const values: Record<string, unknown> = {}
  const listId = templateField.field.list_governance.list_id
  const list = lists.find(l => l.listId === listId)

  if (!list) {
    return { listId: -1, values }
  }

  const firstColumnId = Object.keys(list.columnList)[0]

  if (!firstColumnId) {
    return { listId, values }
  }

  const firstFieldId = list.columnList[firstColumnId]?.fieldId

  if (!firstFieldId) {
    return { listId, values }
  }

  const listValues = list.values.find(
    value => value[firstColumnId] === currentRow.data[firstFieldId]?.value,
  )

  if (!listValues) return { listId, values }

  const iterateDrillDownList = (
    filteredListValues: NestedColumnValue | Record<number, string>,
    columnListIdx: number,
  ) => {
    Object.keys(filteredListValues)
      .filter(columnId => +columnId)
      .forEach(columnId => {
        if (list.columnList[columnId]) {
          values[list.columnList[columnId].fieldId] = filteredListValues[columnId]
          const nextColumnId = Object.keys(list.columnList)[columnListIdx]
          if (nextColumnId && 'children' in filteredListValues) {
            const nextFieldId = list.columnList[nextColumnId]?.fieldId
            const nextChild = nextFieldId
              ? filteredListValues.children.find(
                  value => value[nextColumnId] === currentRow.data[nextFieldId]?.value,
                )
              : null
            if (nextChild) {
              iterateDrillDownList(nextChild, columnListIdx + 1)
            } else {
              for (const column of Object.values(list.columnList)) {
                values[column.fieldId] = ''
              }
            }
          }
        }
      })
  }

  switch (templateField.field.list_governance.lists.type) {
    case 'drop-down':
    case 'dependent':
      for (const [columnId, listValue] of Object.entries(listValues)) {
        const fieldId = list.columnList[columnId]?.fieldId
        if (fieldId) {
          values[fieldId] = listValue
        }
      }
      break
    case 'drill-down':
      iterateDrillDownList(listValues, 1)
      break
    default:
      return { listId, values }
  }

  return {
    listId,
    values,
  }
}

export function generateClassificationColumnFields(columns: { fieldId: number; name: string }[]) {
  return columns.map(({ fieldId, name }) => ({
    id: fieldId,
    field_id: String(fieldId),
    isClassificationColumn: true,
    field: {
      id: fieldId,
      name,
      readOnly: true,
    },
    readOnly: true,
    visible_on_form: true,
  }))
}

interface ClassificationValuesData {
  field_id: string
  cell_value: string[]
  cvid: string
  isClassificationColumn: boolean
}

/**
 * This function is used to build the classification values for the classification columns.
 *
 * @param {array} creativeClassificationColumns - the creative classification columns array
 * @param {string} cvid - row cvid
 * @param {object} classification - the classification object
 *
 * @returns {array} - the classification values
 *
 */
export function buildClassficationValues({
  creativeClassificationColumns,
  classification,
  cvid,
}: {
  creativeClassificationColumns: { fieldId: string | number }[]
  classification?: NonNullable<IDatasetById[string]['rows'][number]['classification']>['summary']
  cvid: string
}): ClassificationValuesData[] {
  return creativeClassificationColumns.map(({ fieldId }) => ({
    field_id: String(fieldId),
    isClassificationColumn: true,
    cell_value: getFlattenCategoryValues(
      classification?.categories?.filter(c => c.categoryId === fieldId),
    ),
    cvid,
  }))
}

export function buildDatasetStructure(
  meta: Pick<IDatasetMetadataResponse, 'id' | 'identifier'>,
  selectedRows: Record<string, ReadGridRow>,
  rows: IDatasetById[string]['rows'],
  creativeClassificationColumns:
    | IDatasetById[string]['columns']
    | IDatasetById[string]['filteredColumns'],
  correctedRows: ErrorCorrectionRow[] = [],
): DatasetWithDatasetRows {
  const creativeClassificationColumnsArray = creativeClassificationColumns ?? []
  return {
    id: meta.id,
    identifier: meta.identifier,
    uuid: uuidv4(),
    updated_at: null,
    creativeClassificationColumns: generateClassificationColumnFields(
      creativeClassificationColumnsArray,
    ),
    dataset_rows: Object.entries(selectedRows).map(([idx, currentRow]) => {
      const idxNumber = Number(idx) - 1
      const cvid = currentRow.cvid || 'cvid_not_found'
      const classificationValues = buildClassficationValues({
        creativeClassificationColumns: creativeClassificationColumnsArray,
        classification: rows[idxNumber]?.classification?.summary,
        cvid,
      })
      const correctedRow = correctedRows.find(row => row.cvid === currentRow.cvid)

      return {
        dataset_values: [
          ...Object.entries(currentRow.data).map(([key, currentCell]) => {
            const correctedCell = correctedRow?.fields[key]
            const cellValue = correctedCell?.modified ? correctedCell.value : currentCell.value
            const errorCorrected =
              !!correctedCell?.modified && currentCell.value !== correctedCell.value

            const datasetValue: DatasetValue = {
              field_id: key,
              cvid,
              cell_value: cellValue,
            }

            // making this field optional so that it is not present without the feature
            if (errorCorrected) {
              datasetValue.errorCorrection = {
                previousValue: currentCell.value,
                modification: cellValue,
                confidence: correctedCell.confidence_level ?? null,
              }
            }

            if (currentCell.comments) {
              datasetValue.comments = currentCell.comments
            }

            return datasetValue
          }),
          ...classificationValues,
        ],
      }
    }),
  }
}
